import React, { useEffect, useState } from 'react';
import { softwareBacklogConfigProviderEntrypoint } from '@jira/ai-rovo_actions-software-backlog/src/softwareBacklogConfigProviderEntrypoint.tsx';
import { softwareVersionDetailConfigProviderEntrypoint } from '@jira/ai-rovo_actions-software-version-detail/src/softwareVersionDetailConfigProviderEntrypoint.tsx';
import { projectVersionsConfigProviderEntrypoint } from '@jira/ai-rovo_actions-project-versions/src/projectVersionsConfigProviderEntrypoint.tsx';
import { issueConfigProviderEntrypoint } from '@jira/ai-rovo_actions-issue/src/issueConfigProviderEntrypoint.tsx';
import { planTimelineConfigProviderEntrypoint } from '@jira/ai-rovo_actions-plan-timeline/src/planTimelineConfigProviderEntrypoint.tsx';
import { permissionsConfigProviderEntrypoint } from '@jira/ai-rovo__actions-permissions/src/permissionsConfigProviderEntrypoint.tsx';
import {
	addRouteListener,
	getRouterState,
} from '@atlassian/react-resource-router/src/controllers/router-store/index.tsx';
import type { RouterState } from '@atlassian/react-resource-router/src/controllers/router-store/types.tsx';
import { AiRovoIssueViewNudgesProvider } from './AiRovoIssueViewNudgesProvider.tsx';
import { AiRovoSoftwareBacklogNudgesProvider } from './AiRovoSoftwareBacklogNudgesProvider.tsx';
import { AiRovoPermissionsNudgesProvider } from './AiRovoPermissionsNudgesProvider.tsx';

/**
 * Add new entrypoints here to the type and to the jiraRouteToConfigProvider object
 *
 * NOTE:
 * You can use any string as a key for the configuration, and base it off of the route name or the query parameters.
 * In the future, you will also be able to set context specific configuration for each route, and link
 * that to the appropriate key for the configuration.
 */
export type RovoEntryPointComponent =
	| typeof softwareBacklogConfigProviderEntrypoint
	| typeof softwareVersionDetailConfigProviderEntrypoint
	| typeof projectVersionsConfigProviderEntrypoint
	| typeof issueConfigProviderEntrypoint
	| typeof planTimelineConfigProviderEntrypoint
	| typeof permissionsConfigProviderEntrypoint;

export const jiraRouteToConfigProvider: Partial<
	Record<
		string,
		{
			entrypoint: RovoEntryPointComponent;
			nudges?: () => React.JSX.Element;
		}
	>
> = {
	'software-backlog': {
		entrypoint: softwareBacklogConfigProviderEntrypoint,
		nudges: () => <AiRovoSoftwareBacklogNudgesProvider />,
	},
	'software-version-detail': {
		entrypoint: softwareVersionDetailConfigProviderEntrypoint,
	},
	'project-versions': {
		entrypoint: projectVersionsConfigProviderEntrypoint,
	},
	issue: {
		entrypoint: issueConfigProviderEntrypoint,
		nudges: () => <AiRovoIssueViewNudgesProvider />,
	},
	'software-plan-timeline': {
		entrypoint: planTimelineConfigProviderEntrypoint,
	},
	'project-settings-software-access': {
		entrypoint: permissionsConfigProviderEntrypoint,
		nudges: () => <AiRovoPermissionsNudgesProvider />,
	},
	'legacy-project-settings-people': {
		entrypoint: permissionsConfigProviderEntrypoint,
		nudges: () => <AiRovoPermissionsNudgesProvider />,
	},
	'legacy-project-settings-permissions': {
		entrypoint: permissionsConfigProviderEntrypoint,
		nudges: () => <AiRovoPermissionsNudgesProvider />,
	},
	'global-settings-issues-permissions-schemes-view': {
		entrypoint: permissionsConfigProviderEntrypoint,
		nudges: () => <AiRovoPermissionsNudgesProvider />,
	},
};

const getConfiguration = (
	routerState: RouterState,
): {
	entrypoint: RovoEntryPointComponent;
	nudges?: () => React.JSX.Element;
} | null => {
	let configurationKey = routerState.route.name;
	/**
	 * Issue view has special case handling because it can be either a standalone issue view or an embed.
	 * Handling the query parameter in case embded is not set.
	 */
	if (
		routerState.route.name === 'issue' ||
		routerState.route.name === 'issue-embed' ||
		routerState.location.search.includes('selectedIssue=')
	) {
		configurationKey = 'issue';
	}

	if (
		configurationKey in jiraRouteToConfigProvider &&
		jiraRouteToConfigProvider[configurationKey]
	) {
		return jiraRouteToConfigProvider[configurationKey] ?? null;
	}
	return null;
};

export const useAiRovoActiveConfiguration = (): {
	entrypoint: RovoEntryPointComponent;
	nudges?: () => React.JSX.Element;
} | null => {
	const [configuration, setConfiguration] = useState<{
		entrypoint: RovoEntryPointComponent;
		nudges?: () => React.JSX.Element;
	} | null>(getConfiguration(getRouterState()));

	useEffect(() => {
		const unsubscribe = addRouteListener((route) => {
			const newConfiguration = getConfiguration(route);
			setConfiguration(newConfiguration);
		});
		return () => unsubscribe();
	}, []);

	return configuration;
};
